import { useState, useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { rememberPath } from '@/lib/last-path';
import Sidebar from './Sidebar';
import BuilderPage from '../../pages/builder/BuilderPage';
import OperatorPage from '../../pages/operator/OperatorPage';
import { TopProgressBar } from '../ui';
import { operatorNavGroups } from '../navigation/operator-nav';
import { SidebarCollapseProvider } from './sidebar-collapse-context';
import { SidebarToggleHotkey } from '../keyboard/SidebarToggleHotkey';
import { useEnsureWelcomeChat } from '../../hooks/useEnsureWelcomeChat';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export default function OperatorLayout() {
  const { pathname, search } = useLocation();
  useEnsureWelcomeChat();
  useEffect(() => {
    rememberPath('operator', pathname + search);
  }, [pathname, search]);
  const isBuilder = pathname === '/builder';
  const isChatView =
    pathname.startsWith('/chat/') &&
    pathname.split('/').length === 3 &&
    pathname !== '/chat/new';
  const isPersistentPage = isBuilder || isChatView;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true',
  );

  const setCollapsed = useCallback((next: boolean) => {
    setSidebarCollapsed(next);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
  }, []);

  const handleToggle = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const collapseValue = useMemo(
    () => ({
      collapsed: sidebarCollapsed,
      setCollapsed,
      toggle: handleToggle,
    }),
    [sidebarCollapsed, setCollapsed, handleToggle],
  );

  return (
    <SidebarCollapseProvider value={collapseValue}>
      <SidebarToggleHotkey />
      <div className="min-h-screen bg-background">
        <TopProgressBar />
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={handleToggle}
          groups={operatorNavGroups}
          brand="operator"
        />
        <div
          className="transition-all duration-500 ease-in-out"
          style={{ paddingLeft: sidebarCollapsed ? '4rem' : '16rem' }}
        >
          {!isPersistentPage && (
            <main className="px-6 pt-[calc(4rem+1.25rem)] pb-6">
              <Outlet />
            </main>
          )}
          <div>
            <BuilderPage visible={isBuilder} />
            <OperatorPage visible={isChatView} />
          </div>
        </div>
      </div>
    </SidebarCollapseProvider>
  );
}
